// User roles and authentication types
export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  AGENCY_ADMIN = 'AGENCY_ADMIN',
  STAFF = 'STAFF',
  CAREGIVER = 'CAREGIVER',
  // HerzensApp module roles
  NURSE = 'NURSE',
  FAMILY = 'FAMILY',
}

// Shifts/Turnusse permission levels
export type ShiftsPermissionLevel = 'none' | 'minimal' | 'full';

export interface UserPermissions {
  dashboard: boolean;
  patients: boolean;
  caregivers: boolean;
  matching: boolean;
  shifts: boolean | ShiftsPermissionLevel; // boolean for backward compatibility, level for granular control
  shiftsChecklistEdit?: boolean; // Can edit checklist content (separate from shifts level)
  invoices: boolean;
  emails: boolean;
  templates: boolean;
  tasks: boolean;
  knowledge: boolean;
  files: boolean;
  staff: boolean;
  settings: boolean;
  billing: boolean;
  agencies: boolean;
  allowedAnsprechpartnerIds?: string[]; // If set, user only sees patients assigned to these Ansprechpartner
  allowedCaregiverAnsprechpartnerIds?: string[]; // If set, user only sees caregivers assigned to these Ansprechpartner
  tasksOnlyAssignedToMe?: boolean; // STAFF only sees tasks assigned to them (default off)
  shiftsOnlyOwnCaregivers?: boolean; // Turnus only shows caregivers this staff member is Ansprechperson Betreuung for (default off)
}

// Helper to normalize shifts permission to level
export function getShiftsPermissionLevel(shifts: boolean | ShiftsPermissionLevel | undefined): ShiftsPermissionLevel {
  if (shifts === true || shifts === 'full') return 'full';
  if (shifts === 'minimal') return 'minimal';
  return 'none';
}

// Permission check helpers for shifts
export function canModifyShifts(permissions: UserPermissions | undefined): boolean {
  if (!permissions) return false;
  const level = getShiftsPermissionLevel(permissions.shifts);
  return level === 'full';
}

export function canSendInvoice(permissions: UserPermissions | undefined): boolean {
  if (!permissions) return false;
  const level = getShiftsPermissionLevel(permissions.shifts);
  return level === 'full';
}

export function canUseKontrolliert(permissions: UserPermissions | undefined): boolean {
  if (!permissions) return false;
  const level = getShiftsPermissionLevel(permissions.shifts);
  return level === 'minimal' || level === 'full';
}

export function canAccessChecklist(permissions: UserPermissions | undefined): boolean {
  if (!permissions) return false;
  const level = getShiftsPermissionLevel(permissions.shifts);
  return level === 'minimal' || level === 'full';
}

export function canEditChecklistContent(permissions: UserPermissions | undefined): boolean {
  if (!permissions) return false;
  const level = getShiftsPermissionLevel(permissions.shifts);
  // Must have at least minimal shifts access AND explicit checklist edit permission
  return (level === 'minimal' || level === 'full') && permissions.shiftsChecklistEdit === true;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  agencyId?: string;
  isActive: boolean;
  permissions?: UserPermissions;
  createdAt: string;
  updatedAt: string;
  /** All agencies this login may open (one backend users row each). */
  memberships?: import('../utils/activeMembership').AgencyMembership[];
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Agency types
export interface Agency {
  id: string;
  name: string;
  // Address
  addressStreet: string;
  addressHouseNumber: string;
  addressStaircase?: string;
  addressFloor?: string;
  addressDoor?: string;
  addressAdditionalInfo?: string;
  addressPostalCode: string;
  addressCity: string;
  addressCountry: string;
  /** EU VAT ID (UID) — required for reverse charge on cross-border EU B2B. */
  vatId?: string;
  /**
   * @deprecated kept for backward compatibility. Use granular address fields instead.
   */
  address?: string;
  phone: string;
  email: string;
  licenseNumber: string;
  isActive: boolean;
  /** GAV-Zeiterfassung (Schweiz) — vom Super-Admin pro Agentur freigeschaltet. */
  gavTimeTrackingEnabled?: boolean;
  /** Kunden-API (REST + MCP) — vom Super-Admin pro Agentur freigeschaltet. */
  apiAccessEnabled?: boolean;
  adminUserId: string;
  adminUser?: User;
  logoUrl?: string; // Firebase Storage URL for agency logo
  signatureDataUrl?: string | null; // Stored agency signature (PNG data URL) for one-click signing
  showLogoOnInvoices?: boolean; // Whether to display logo on invoices
  logoSizeOnInvoices?: number; // Logo size in pixels for invoices (40-150)
  /** HerzensApp: Hinweis auf offene/verschobene Aufgaben (Standard an). */
  herzensappOpenTaskWarning?: boolean;
  /** Ausweis-/Einkommensnachweis per OCR vorausfuellen (Standard an). */
  documentOcrEnabled?: boolean;
  /** Gewerbestatus (GISA/WKO, nur AT) — vom Super-Admin freigeschaltet. */
  gewerbeCheckEnabled?: boolean;
  createdAt: string;
  updatedAt: string;
  // Invoice Settings
  invoiceBankName?: string;
  invoiceBankIban?: string;
  invoiceBankBic?: string;
  invoiceNumberPrefix?: string;
  invoiceHeader?: string;
  invoiceFooter?: string;
  manualInvoiceDescription?: string;
  invoiceServiceLabel?: string;
  invoiceBillingAddress?: string;
  invoiceVatNumber?: string;
  invoiceDueDateDays?: number;
  /** 'days' = Rechnungsdatum + N Tage (Standard), 'day_of_month' = fester Zahltag. */
  invoiceDueMode?: 'days' | 'day_of_month';
  /** Fester Zahltag 1–31, nur bei invoiceDueMode = 'day_of_month'. */
  invoiceDueDayOfMonth?: number | null;
  invoiceCounter?: number;
  // VAT Settings
  vatRate?: number;
  vatIncluded?: boolean;
  vatEnabled?: boolean;
  // Default Daily Fee Settings
  defaultDailyFeeToAgency?: number;
  // Company information
  companyRegisterNumber?: string;
  // Managing Director information
  mdFirstName?: string;
  mdLastName?: string;
  // Bank account owner (different from bank name)
  bankAccountOwner?: string;
  // Second bank account (optional) — selectable per Rechnungskreis
  invoiceBankName2?: string;
  invoiceBankIban2?: string;
  invoiceBankBic2?: string;
  bankAccountOwner2?: string;
  // Name und Anschrift Bevollmächtigte
  bevollmaechtigte?: string;
  // Email Template Settings
  emailTemplateSubject?: string;
  emailTemplateMessage?: string;
  emailTemplateEnabled?: boolean;
  // Signature Email Template Settings
  signatureEmailTemplateSubject?: string;
  signatureEmailTemplateMessage?: string;
  signatureEmailTemplateEnabled?: boolean;
  signatureLinkDownloadEnabled?: boolean;
  shiftOverlapWarningsEnabled?: boolean;
  shiftAllowParallelCaregivers?: boolean;
  /** Honorar on the checklist card: 'off' | 'caregiver' (default) | 'client' */
  checklistFeeSource?: 'off' | 'caregiver' | 'client';
  /** Einstellungen → Turnus: Standardwerte jedes neuen Turnus (siehe useShiftFormDefaults). */
  turnusDefaults?: Record<string, unknown> | null;
  /** Rechnungs-Symbol der Turnusliste: sofort senden (true, Standard) oder nur Entwurf. */
  turnusInvoiceIconSends?: boolean;
  /** Wechsel-Export: aktive Spalten (CHANGEOVER_EXPORT_COLUMNS); null/leer = alle. */
  changeoverExportColumns?: string[] | null;
  // Auto-send signed contract after signature events (both default true)
  sendEmailAfterSignature?: boolean;
  sendEmailAfterAllSignatures?: boolean;
  signatureLinkSendCcToBetreuerAnsprechpartner?: boolean;
  signatureSingleSendCcToBetreuerAnsprechpartner?: boolean;
  signatureAllSendCcToBetreuerAnsprechpartner?: boolean;
  signatureLinkSendCcToKlientAnsprechpartner?: boolean;
  signatureSingleSendCcToKlientAnsprechpartner?: boolean;
  signatureAllSendCcToKlientAnsprechpartner?: boolean;
  documentSendCcToBetreuerAnsprechpartner?: boolean;
  documentSendCcToKlientAnsprechpartner?: boolean;

  // Invoice File Naming Settings
  invoiceFilenameTemplate?: string;
  invoiceEmailSubjectTemplate?: string;
  // Multiple Invoice Prefixes
  invoicePrefixes?: Array<{
    prefix: string;
    counter: number;
    label: string;
    bankAccount?: 1 | 2;
  }>;
  // Default prefix for manual shift invoices
  defaultManualShiftPrefix?: string;
  // Quick Add Templates for invoice line items
  quickAddTemplates?: Array<{ description: string; amount: number; category?: string; taxRate?: number | null }>;
  // Theme/Branding Settings
  primaryColor?: string; // Primary theme color in hex format (e.g., #f24b6a)
  // BMD Export Settings
  bmdGkto?: string;
  bmdSymbol?: string;
  bmdBucod?: string;
  bmdMwst?: string;
  bmdSteucod?: string;
  bmdGegenbuchkz?: string;
  bmdVerbuchkz?: string;
  // Custom SMTP Settings
  smtpEnabled?: boolean;
  smtpHost?: string;
  smtpPort?: number;
  smtpSecure?: boolean;
  smtpUsername?: string;
  /** Nur zum Speichern — das Backend liefert das Passwort nie zurück. */
  smtpPassword?: string;
  /** Ob ein SMTP-Passwort hinterlegt ist. */
  smtpPasswordSet?: boolean;
  smtpFromEmail?: string;
  smtpFromName?: string;
  customDocumentTypes?: Array<{ id: string; caption: string }>;
  /** Reihenfolge/Ausblenden der festen Betreuer-Dokumente; null = Standard. */
  caregiverStandardDocuments?: Array<{ key: string; hidden?: boolean }> | null;
  /** Frei benannte E-Mail-Vorlagen der Agentur. */
  customEmailTemplates?: CustomEmailTemplate[];
  /** null = nie gepflegt (Standardtypen); [] = bewusst alle entfernt. */
  patientCustomDocumentTypes?: Array<{ id: string; caption: string }> | null;
  /** Wissen: eigene Eintragstypen + Fragen-Vorlagen je Typ */
  knowledgeEntryTypes?: Array<{ key: string; label?: string; questions: string[] }>;
  aiSearchEnabled?: boolean;
  clientOnboardingTemplates?: Array<{ id: string; name: string; fields: string[] }>;
  // Matching (Betreuungsanfrage) field templates, shared across the agency
  matchingClientFieldTemplates?: Array<{ id: string; name: string; fields: string[] }>;
  matchingCaregiverFieldTemplates?: Array<{ id: string; name: string; fields: string[] }>;
  /** Prepared share texts for the public matching link (WhatsApp, Facebook, …) */
  matchingShareTemplates?: Array<{ id: string; name: string; channel: string; text: string }>;
  /** Default row order of the Dienstplan: lastName | createdAtAsc | createdAtDesc */
  turnusDefaultSort?: 'lastName' | 'createdAtAsc' | 'createdAtDesc';
  invoiceEmailMessage?: string;
  invoiceSendCcToBetreuerAnsprechpartner?: boolean;
  /** Verrechnungsarten (creationMode) mit "Honorar"/"Honorarnummer" statt "Rechnung" auf dem Beleg. */
  invoiceHonorarModes?: Array<'caregiver-to-client' | 'agency-to-client' | 'agency-to-caregiver'> | null;
  /** Master switch for outbound invoice emails (invoice, Mahnung, Freigegeben). */
  invoiceEmailEnabled?: boolean;
  invoiceShowPatientDebitorennummer?: boolean;
  /** Hauptfarbe der Rechnungs-PDFs (#rrggbb); null = Standard #002766. */
  invoicePrimaryColor?: string | null;
  caregiverInvoiceUseClientAddress?: boolean;
  /** Eigener Rechnungskreis je Betreuer:in (Betreuer → Klient-Rechnungen). */
  caregiverInvoiceCirclesEnabled?: boolean;
  /** EPC-QR-Code "Zahlen mit Code" auf Rechnungs-PDFs. */
  invoicePaymentQrEnabled?: boolean;
  /** Rechnungswährung der Agentur ('EUR' | 'CHF'). */
  invoiceCurrency?: 'EUR' | 'CHF';
  /** Land der Agentur — nur Super-Admin; bestimmt die Standardwährung (CH → CHF). */
  country?: AgencyCountry;
  /** E-Mail-Signatur (Einstellungen → E-Mail-Signatur), im Backend gesäubert. */
  emailSignatureEnabled?: boolean;
  emailSignatureHtml?: string | null;
  emailSignatureText?: string | null;
  invoiceCaregiverFeeNote?: string | null;
  invoiceCollectionFootnote?: string | null;
  invoiceDirectDebitNotice?: string | null;
  mahnungEmailSubject?: string;
  mahnungEmailMessage?: string;
  /** CC the caregiver's Ansprechpartner on Mahnungen (separate from the invoice setting). */
  mahnungSendCcToBetreuerAnsprechpartner?: boolean;
  freigegebenEmailSubject?: string;
  freigegebenEmailMessage?: string;
}

// Contact Person type for patients
export interface ContactPerson {
  relationshipContactType: string;
  name: string;
  phoneNumber?: string;
  mobileNumber?: string;
  email?: string;
}

// Contract-related types for new Vertragliches section
export interface ContractDuration {
  wert: number;
  einheit: 'tage' | 'wochen' | 'monate' | 'jahre';
}

export interface ContractTurnusDuration {
  wert: number;
  einheit: 'tage' | 'wochen' | 'monate';
}

export interface ContractPersonalAssignment {
  recruiter_id?: string | null;
  betreuer1_id?: string | null;
  betreuer2_id?: string | null;
}

export interface ContractReminder {
  aktiv: boolean;
  vorlauf?: {
    wert: number;
    einheit: 'tage' | 'wochen';
  };
  email?: string;
  cases_abwechselnd?: boolean;
}

export interface PatientContract {
  startdatum: string; // ISO date string
  laufzeit: ContractDuration;
  turnuslaenge: ContractTurnusDuration;
  personalzuweisung?: ContractPersonalAssignment;
  auto_rechnung_aktiv?: boolean;
  kostenart?: 'gebuehr_agentur' | 'tagessatz_klient';
  erinnerung_vor_vertragsende?: ContractReminder;
}

/**
 * Client category. "potential" (Interessent) is intentionally NOT active:
 * such clients are not billed and stay out of the Dienstplan and dropdowns.
 */
export type ClientStatus = 'active' | 'inactive' | 'potential';

/**
 * Caregiver category. "dormant" (Ruhend) is intentionally NOT active: like an
 * inactive caregiver it stays out of dropdowns, Turnus planning and counts.
 */
export type CaregiverStatus = 'active' | 'inactive' | 'dormant';

// Comprehensive Patient type based on schema
/**
 * Fields a migration carried over that have no equivalent in our own schema.
 * Kept verbatim and shown read-only, so an import never silently drops data.
 */
export interface ExternalImportData {
  source: string;
  importedAt: string;
  partyId: string | null;
  raw: Record<string, string>;
}

export interface Patient {
  id: string;
  /** Zahlt per Einzugsermächtigung (Rechnungshinweis "NICHT EINZAHLEN", kein Zahl-QR-Code). */
  sepaDirectDebit?: boolean;

  // Basic Information
  clientId: string;
  agencyId: string;
  debitorennummer?: string;
  firstName: string;
  lastName: string;
  birthName?: string;
  /** Firebase path of the profile photo — presence means "has a photo". */
  photoPath?: string;
  email: string;
  dateOfBirth?: string;
  gender?: string;
  citizenship?: string;
  phoneNumberMobile?: string;
  phoneNumberFixed?: string;
  salutation?: string;

  // Address Information
  addressStreet?: string;
  addressHouseNumber?: string;
  addressStaircase?: string;
  addressFloor?: string;
  addressDoor?: string;
  addressAdditionalInfo?: string;
  addressPostalCode?: string;
  addressCity?: string;
  addressState?: string;
  addressCountry?: string;
  /** @deprecated */
  addressHouseNumberStiegeDoor?: string;

  // Medical Information
  svnrClient?: string;
  insuranceNumber?: string;
  insuranceType?: string;
  careLevelAustria?: string;
  careLevelBescheidDate?: string;
  isSmoker?: boolean;
  isDelegation?: boolean;
  delegationNote?: string;
  // Stammdaten: Förderung (ja/nein), Vollmacht (ja/nein), Agentur Start (date)
  foerderung?: boolean;
  vollmacht?: boolean;
  agencyStartDate?: string;
  primaryLanguage?: string;
  otherLanguagesSpoken?: string;
  weightKg?: number;
  heightCm?: number;
  hasAllergies?: boolean;
  pflegegeldApplicationStatus?: string;
  pflegegeldIncreaseApplicationStatus?: string;
  clientBankIban?: string;
  clientBankBic?: string;
  transportCost?: number;
  createdVia?: string | null;
  onboardingSubmittedAt?: string | null;
  /** Primary key in the source system when this record came from a migration. */
  manacarePartyId?: string | null;
  clientType?: string | null;
  clientTypeFreeText?: string | null;
  /** Verbatim source columns that have no field of their own. Read-only. */
  externalImportData?: ExternalImportData | null;
  currentDailyRate?: number;
  currentDailyRateAgency?: number;
  weeklyRateAgency?: number | null;
  monthlyRateAgency?: number | null;
  monthlyNetIncome?: number | null;
  maritalStatus?: string;
  religion?: string;
  placeOfBirthStreet?: string;
  placeOfBirthPostalCode?: string;
  placeOfBirthCity?: string;

  // Care Needs
  nightCareIsNeeded?: boolean;
  mobilityStandingAbility?: string;
  mobilityWalkingAbility?: string;
  mobilityGettingUpAbility?: string;
  careNeedsDescription?: string;

  // Mobility Aids
  wheelchair?: boolean;
  rollator?: boolean;

  // Additional Notes for Each Section
  stammdatenNotes?: string;
  vertraglichesNotes?: string;
  kontakteVertretungNotes?: string;
  wohnsituationNotes?: string;
  medizinischeInformationenNotes?: string;
  medikamenteMedizinischePflegeNotes?: string;
  mobilitaetTaeglichePflegeNotes?: string;
  ernaehrungFluessigkeitNotes?: string;
  ausscheidungKontinenzNotes?: string;
  kommunikationOrientierungNotes?: string;
  nachtbetreuungNotes?: string;
  koerperpflegeHygieneNotes?: string;
  haushaltAlltagNotes?: string;
  sozialeKontakteFreizeitNotes?: string;

  // Living Situation
  caregiverAccomOwnBedroom?: boolean;
  caregiverAccomOwnBathroom?: boolean;
  caregiverAccomInternetAccess?: boolean;
  livingSituationHasPets?: boolean;

  // Medical Conditions
  hasDementia?: boolean;
  hasStroke?: boolean;
  hasParalysis?: boolean;
  hasDiabetes?: boolean;
  isInsulinDependent?: boolean;

  // Representative Person
  representativeType?: string;
  representativeSalutation?: string;
  /** Vorangestellter Titel des Vertreters ("Dr.", "Mag.") */
  representativeTitle?: string;
  titleBefore?: string;
  titleAfter?: string;
  representativeFirstName?: string;
  representativeLastName?: string;
  representativeBirthDateOrSvnr?: string;
  representativeSvnr?: string;
  representativeAddressStreet?: string;
  representativeAddressHouseNumber?: string;
  representativeAddressStaircase?: string;
  representativeAddressFloor?: string;
  representativeAddressDoor?: string;
  representativeAddressPostalCode?: string;
  representativeAddressCity?: string;
  representativeAddressCountry?: string;
  representativePhoneNumber?: string;
  representativeEmail?: string;
  useRepresentativeEmail?: boolean;

  // Contact Persons
  contactPersons?: ContactPerson[];

  // Contract Information (Vertragliches)
  vertragliches?: PatientContract;

  // Living Circumstances
  housingType?: string;
  barriersInLivingArea?: boolean;
  balconyTerraceAvailable?: boolean;
  gardenAvailable?: boolean;
  keySafe?: boolean;
  babyMonitor?: boolean;
  bathroomWithShower?: boolean;
  bathroomWithBathtub?: boolean;
  apartmentCondition?: string;
  livesAlone?: boolean;
  livesWith?: string[];
  additionalCareNeededPersonInHousehold?: string;
  hasCat?: boolean;
  hasDog?: boolean;
  otherPets?: string;

  // Existing Support
  medicalHomeCare?: boolean;
  physioOrErgoTherapy?: boolean;
  palliativeTeam?: boolean;
  cleaningService?: boolean;

  // 24h Care Conditions
  ownRoomForCaregiver?: boolean;
  dailyRestTimeSecured?: boolean;
  replacementDuringLeisureVacation?: boolean;
  sharedCookingEating?: boolean;
  foodBudgetAgreed?: boolean;
  foodBudgetAmount?: number;

  // Assessment Data
  assessmentData?: any;

  // Staff Assignment - Patient can have up to 2 assigned staff members for supervision
  assignedStaffId?: string;
  assignedStaff2Id?: string;
  assignedStaff1Description?: string;
  assignedStaff2Description?: string;
  assignedStaffName?: string; // For display purposes
  assignedStaff2Name?: string; // For display purposes

  // Stop Trigger - For contract termination (death, contract end, etc.)
  contractStopDate?: string | null;
  stopTriggerReason?: string | null;

  // Betreuerwunsch / Matchingkriterien - 5 free-text fields (Vertragsdaten)
  caregiverMatching1?: string | null;
  caregiverMatching2?: string | null;
  caregiverMatching3?: string | null;
  caregiverMatching4?: string | null;
  caregiverMatching5?: string | null;

  // Custom documents (references agency-level patientCustomDocumentTypes)
  customDocuments?: Array<{ id: string; caption: string; fileId?: string }>;

  // Additional fields
  notes?: string;
  /**
   * Derived from clientStatus — true only for "active". Potential clients are
   * isActive = false so billing, Dienstplan and the client dropdowns skip them.
   */
  isActive: boolean;
  clientStatus?: ClientStatus;
  createdAt: string;
  updatedAt: string;
}

// Patient form data types
export interface CreatePatientFormData extends Omit<Patient, 'id' | 'clientId' | 'agencyId' | 'createdAt' | 'updatedAt'> {
  clientId?: string;
  agencyId?: string;
}
export interface UpdatePatientFormData extends Partial<CreatePatientFormData> {}

// Patient query parameters
export interface PatientQueryParams {
  search?: string;
  staffNameSearch?: string;
  agencyId?: string;
  isActive?: boolean;
  clientStatus?: ClientStatus;
  careLevelAustria?: string;
  hasDementia?: boolean;
  nightCareIsNeeded?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  // Comma-separated entity property names to return instead of the full record.
  // Backend whitelists and always includes id (+ staff-id columns for name
  // resolution). Distinct `fields` values produce distinct query-cache entries.
  fields?: string;
}

// Patient statistics
export interface PatientStatistics {
  totalPatients: number;
  activePatients: number;
  inactivePatients: number;
  potentialPatients?: number;
  patientsWithDementia: number;
  patientsNeedingNightCare: number;
  careLevelDistribution: Array<{
    careLevel: string;
    count: number;
  }>;
}

// Legacy types for backward compatibility
export interface EmergencyContact {
  name: string;
  relationship: string;
  phone: string;
  email?: string;
}

export interface MedicalInfo {
  conditions: string[];
  medications: string[];
  allergies: string[];
  specialInstructions?: string;
}

// Previous employer interface for caregiver professional profile
export interface PreviousEmployer {
  employerName: string;
  position: string;
  startDate: string;
  endDate: string;
  responsibilities: string;
}

// Language proficiency interface
export interface LanguageProficiency {
  language: string;
  proficiency: 'Basic' | 'Intermediate' | 'Advanced' | 'Native';
}

// Professional certification interface
export interface ProfessionalCertification {
  certificationName: string;
  issuingOrganization: string;
  issueDate: string;
  expiryDate: string;
  certificationNumber: string;
}

// Medical training certificate interface
export interface MedicalTrainingCertificate {
  trainingName: string;
  provider: string;
  completionDate: string;
  hoursCompleted: number;
}

// Accommodation requirements interface
export interface AccommodationRequirements {
  requiresPrivateRoom: boolean;
  requiresPrivateBathroom: boolean;
  requiresInternetAccess: boolean;
}

export interface DiseaseExperience {
  'Demenz'?: boolean;
  'Dekubitus'?: boolean;
  'Lungenerkrankung (chronisch)'?: boolean;
  'Diabetes'?: boolean;
  'Depression'?: boolean;
  'Kachexie'?: boolean;
  'Allergien'?: boolean;
  'Schlaganfall'?: boolean;
  'Parkinson'?: boolean;
  'Tumorerkrankung (Krebserkrankung)'?: boolean;
  'Osteoporose'?: boolean;
  'Andere psychische Störungen'?: boolean;
  'Adipositas'?: boolean;
  'Infektiöse Krankheiten'?: boolean;
  'Lähmung'?: boolean;
  'Herzerkrankung'?: boolean;
  'Knochenbruch'?: boolean;
  'Multiple Sklerose'?: boolean;
  'COPD'?: boolean;
  'Amputationen'?: boolean;
  'Übermäßiger Alkohol- oder Drogenkonsum (stark alkoholisiert oder Drogenkonsum)'?: boolean;
}

// Comprehensive Caregiver interface based on schema
export interface Caregiver {
  id: string;

  // Basic Information
  caregiverId: string;
  agencyId: string;
  firstName: string;
  lastName: string;
  birthName?: string;
  fatherName?: string;
  email: string;
  phoneNumber?: string;
  dateOfBirth?: Date | string;
  birthCity?: string;
  gender?: 'Male' | 'Female' | 'Other';
  profileDescription?: string;
  profilePictureUrl?: string;
  /** Firebase path of the uploaded profile photo (profilePictureUrl stays legacy). */
  photoPath?: string;

  // Address Information
  addressStreet?: string;
  addressHouseNumber?: string;
  addressStaircase?: string;
  addressFloor?: string;
  addressDoor?: string;
  addressAdditionalInfo?: string;
  addressPostalCode?: string;
  addressCity?: string;
  addressCountry?: string;
  secondaryAddressStreet?: string;
  secondaryAddressHouseNumber?: string;
  secondaryAddressStaircase?: string;
  secondaryAddressFloor?: string;
  secondaryAddressDoor?: string;
  secondaryAddressAdditionalInfo?: string;
  secondaryAddressPostalCode?: string;
  secondaryAddressCity?: string;
  secondaryAddressCountry?: string;
  addressState?: string;
  secondaryAddressState?: string;

  // Gewerbestandort — Adresse der Gewerbeanmeldung (Gewerbeschein/WKO).
  businessAddressStreet?: string;
  businessAddressHouseNumber?: string;
  businessAddressPostalCode?: string;
  businessAddressCity?: string;
  businessAddressState?: string;
  businessAddressCountry?: string;

  // Administrative Information
  verfuegbarkeit?: Date | string;
  debitorennummer?: string;
  gewicht?: number;
  groesse?: number;

  // Insurance Information
  insuranceNumber?: string;
  insuranceType?: string;

  // Personal Information
  svnr?: string;
  citizenship?: string;
  maritalStatus?: 'Single' | 'Married' | 'Divorced' | 'Widowed' | 'Other';
  religion?: string;
  isSmoker?: boolean;
  smokingFrequency?: 'up_to_5' | '5_to_10' | 'more_than_10';
  hasDrivingLicense?: boolean;
  drivingLicenseNotes?: string;
  timeZone?: string;
  bodyType?: 'Strong/Muscular' | 'Small and Slim' | 'Average Build' | 'Other (Please Specify)';
  bodyTypeOther?: string;

  // Professional Information
  certifications?: string;
  yearsInProfession?: number;
  germanProficiency?: 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2' | 'Native';
  isAvailableWeekends?: boolean;
  isFullTime?: boolean;
  defaultRotationPeriodDays?: number;
  agencyStartDate?: string;

  // Financial Information
  bankIban?: string;
  bankBic?: string;
  /** Eigener Rechnungskreis (nur wirksam, wenn die Agentur ihn aktiviert hat). */
  invoicePrefix?: string | null;
  invoiceCounter?: number;
  dailyRate?: number;
  dailyFeeToAgency?: number;

  commercialRegNo?: string;
  /** Vertragsmodell (deutsche Agenturen): self_employed | posted | employed_household | employed_agency */
  employmentModel?: string | null;

  // Emergency Contact
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRaw?: string;

  // Work Preferences
  preferredWorkRegions?: string[];
  specializations?: ('Dementia Care' | 'Palliative Care' | 'Mobility Assistance' | 'Medication Management' | 'Post-Surgery Care' | 'Diabetes Care' | 'Stroke Recovery' | 'General Elder Care')[];

  // Legal Compliance
  dsgvoConsentGiven: boolean;
  workPermitStatus?: 'EU Citizen' | 'Work Permit' | 'Temporary Permit' | 'Pending';
  travelingDocumentType?: 'Passport' | 'Identification Card' | 'Driving License' | 'Other';

  // Passport-specific fields
  passportNumber?: string;
  passportDateOfIssue?: Date | string;
  passportIssuingAuthority?: string;
  passportState?: string;
  passportExpiryDate?: Date | string;
  passportNotes?: string;

  // ID Card-specific fields
  idCardNumber?: string;
  idCardDateOfIssue?: Date | string;
  idCardIssuingAuthority?: string;
  idCardState?: string;
  idCardExpiryDate?: Date | string;
  idCardNotes?: string;

  // Driving License-specific fields (as travel document)
  drivingLicenseNumber?: string;
  drivingLicenseDateOfIssue?: Date | string;
  drivingLicenseIssuingAuthority?: string;
  drivingLicenseState?: string;
  drivingLicenseExpiryDate?: Date | string;
  drivingLicenseDocumentNotes?: string;

  // Other document type fields
  otherDocumentNumber?: string;
  otherDocumentDateOfIssue?: Date | string;
  otherDocumentIssuingAuthority?: string;
  otherDocumentState?: string;
  otherDocumentExpiryDate?: Date | string;
  otherDocumentNotes?: string;

  // General document info (deprecated, kept for backward compatibility)
  documentInfo?: string;

  backgroundCheckCompleted?: boolean;
  referencesVerified?: boolean;

  // Professional Profile
  totalYearsExperience?: number;
  previousEmployers?: PreviousEmployer[];
  languages?: LanguageProficiency[];
  specialMedicalTraining?: string[];
  professionalCertifications?: ProfessionalCertification[];
  medicalTrainingCertificates?: MedicalTrainingCertificate[];

  // Performance Metrics
  clientSatisfactionRating?: number;
  totalAssignmentsCompleted?: number;
  attendanceRate?: number;
  punctualityRating?: number;
  professionalismRating?: number;

  // Preferences and Constraints
  preferredPatientTypes?: ('Elderly (65+)' | 'Middle-aged (40-64)' | 'Young adults (18-39)' | 'Dementia patients' | 'Post-surgery patients' | 'Terminal care patients' | 'Mobility-impaired patients')[];
  workEnvironmentPreferences?: ('Private homes' | 'Assisted living facilities' | 'Nursing homes' | 'Rehabilitation centers')[];
  maximumTravelDistance?: number;
  accommodationRequirements?: AccommodationRequirements;

  // Special Care Conditions
  mehrpersonenhaushalt?: boolean;
  fuerMehrerePersonenSorgen?: boolean;
  kannFuerMaennerSorgen?: boolean;
  mitHaustierenEinverstanden?: boolean;
  /** Je Tierart: 'like' | 'neutral' | 'allergic'. Der Boolean oben kann keine Allergie ausdruecken. */
  petPrefDogs?: string | null;
  petPrefCats?: string | null;
  petPrefFarmAnimals?: string | null;
  petPrefSmallAnimals?: string | null;
  /** Als Vertretung fuer einen fremden Turnus buchbar. */
  isRepresentationBookable?: boolean;
  manacarePartyId?: string | null;
  externalImportData?: ExternalImportData | null;

  // Experience with challenging care conditions
  careMultiplePeople?: boolean;
  transferImmobileWithHelp?: '<50' | '50-70' | '>70';
  transferImmobileWithoutHelp?: '<50' | '50-70' | '>70';
  nightAwakening?: 'nein' | '1-2' | 'öfters';
  dementiaExperience?: 'begonnen' | 'fortgeschrittene' | 'mit hoher Wahrscheinlichkeit';
  experienceWithLifter?: boolean;
  diseaseExperience?: DiseaseExperience;

  // Experience with medical activities
  hypodermicInjections?: boolean;
  permanentCatheter?: boolean;
  colostomy?: boolean;
  pegTube?: boolean;
  diabetesInsulinDependent?: boolean;
  /** Lehnt die Übernahme delegierter Tätigkeiten ab */
  delegationRefused?: boolean;

  // Special skills
  dgkpInHomeCountry?: boolean;
  cooking?: boolean;
  physicalActivation?: boolean;
  mentalActivation?: boolean;

  // Staff Assignment - Caregiver can have 1 assigned staff member for supervision
  assignedStaffId?: string;
  assignedStaffDescription?: string;
  assignedStaffName?: string; // For display purposes

  // Document Fields
  pflegekurszeugnisFile?: string;
  pflegekurszeugnisUebersetzungFile?: string;
  dgkpDiplomFile?: string;
  dgkpDiplomUebersetzungFile?: string;
  ecardFile?: string;
  ecardBackFile?: string;
  gewerbescheinFile?: string;
  strafregisterauszugFile?: string;
  strafregisterauszugUebersetzungFile?: string;
  ausweisFile?: string;
  ausweisBackFile?: string;
  fuehrerscheinFile?: string;
  fuehrerscheinBackFile?: string;
  customDocuments?: Array<{ id: string; caption: string; fileId?: string }>;

  // References (up to 3)
  reference1Name?: string;
  reference1Phone?: string;
  reference1Note?: string;
  reference2Name?: string;
  reference2Phone?: string;
  reference2Note?: string;
  reference3Name?: string;
  reference3Phone?: string;
  reference3Note?: string;

  // Additional Notes for Each Section
  administrativeNotes?: string;
  optionalInfoNotes?: string;
  professionalInfoNotes?: string;
  financialInfoNotes?: string;
  emergencyContactNotes?: string;
  legalComplianceNotes?: string;
  referencesNotes?: string;
  documentsNotes?: string;

  // Additional fields
  notes?: string;
  /** Derived from caregiverStatus — true only for "active". */
  isActive: boolean;
  /** Aktiv / Ruhend / Inaktiv; may be missing on projected or legacy payloads. */
  caregiverStatus?: CaregiverStatus | null;
  createdAt: string;
  updatedAt: string;
  lastActiveDate?: string;
}

// Caregiver form data types
export interface CreateCaregiverFormData extends Omit<Caregiver, 'id' | 'caregiverId' | 'agencyId' | 'createdAt' | 'updatedAt'> {
  caregiverId?: string;
  agencyId?: string;
}
export interface UpdateCaregiverFormData extends Partial<CreateCaregiverFormData> {}

// Caregiver query parameters
export interface CaregiverQueryParams {
  search?: string;
  agencyId?: string;
  isActive?: boolean;
  caregiverStatus?: CaregiverStatus;
  /** Gewerbestatus-Ampel (nur AT, freigeschaltete Agenturen). */
  gewerbeAmpel?: 'green' | 'yellow' | 'red' | 'grey';
  germanProficiency?: string;
  isAvailableWeekends?: boolean;
  isFullTime?: boolean;
  workPermitStatus?: string;
  specializations?: string[];
  assignedStaffId?: string;
  staffNameSearch?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  // Comma-separated entity property names to return instead of the full record.
  // Backend whitelists and always includes id. Distinct `fields` values produce
  // distinct query-cache entries.
  fields?: string;
}

// Caregiver statistics
export interface CaregiverStatistics {
  totalCaregivers: number;
  activeCaregivers: number;
  inactiveCaregivers: number;
  /** "Ruhend" — a subset of inactiveCaregivers. */
  dormantCaregivers?: number;
  fullTimeCaregivers: number;
  weekendAvailableCaregivers: number;
  germanProficiencyDistribution: Array<{
    proficiency: string;
    count: number;
  }>;
  specializationDistribution: Array<{
    specialization: string;
    count: number;
  }>;
}

// Shift types
export enum ShiftCostType {
  DAILY_RATE = 'dailyRate',
  DAILY_FEE_TO_AGENCY = 'dailyFeeToAgency',
  NONE = 'none',
  AGENCY_FEE_TO_CLIENT = 'agencyFeeToClient',
  AGENCY_WITH_CAREGIVER_FEE = 'agencyWithCaregiverFee',
}

export interface Shift {
  id: string;
  patientId: string;
  caregiverId: string;
  startTime: string;
  endTime: string;
  status: ShiftStatus;
  notes?: string;
  agencyId: string;
  costType: ShiftCostType;
  createdAt: string;
  updatedAt: string;
}

export enum ShiftStatus {
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

// Invoice types
export enum InvoiceStatus {
  DRAFT = 'draft',
  SENT = 'sent',
  PAID = 'paid',
  OVERDUE = 'overdue',
  CANCELLED = 'cancelled',
  FREIGEGEBEN = 'freigegeben',
  ABGESCHLOSSEN = 'abgeschlossen',
}

export enum InvoiceType {
  AUTOMATIC = 'automatic',
  MANUAL = 'manual',
  FAMILY = 'family',
}

export enum InvoicePricingMode {
  DAILY_RATE = 'daily_rate',
  ITEMS_ONLY = 'items_only',
  MIXED = 'mixed',
}

/** Netto/USt./Brutto genau wie in der Rechnungs-PDF (vom Backend berechnet). */
export interface InvoiceDisplayTotals {
  net: number;
  tax: number;
  gross: number;
}

export interface Invoice {
  /** Gesamtbetrag wie in der PDF — Listen zeigen diesen statt selbst zu rechnen. */
  displayTotals?: InvoiceDisplayTotals;
  id: string;
  invoiceNumber: string;
  type: InvoiceType;
  pricingMode?: InvoicePricingMode;
  status: InvoiceStatus;
  patientId?: string;
  caregiverId?: string;
  shiftId?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  totalDays: number;
  dailyRate: number;
  totalAmount: number;
  invoiceDate: string;
  dueDate?: string;
  sentAt?: string;
  paidAt?: string;
  paymentMethod?: string;
  paymentReference?: string;
  notes?: string;
  invoiceRecipient?: string;
  creationMode?: string | null;
  /** Verrechnungsmodell des Turnus, aus dem die Rechnung entstand. */
  shiftCostType?: string | null;
  familyScheduleId?: string | null;
  /** Entwurf einer wiederkehrenden Rechnung mit "Vor dem Versenden prüfen". */
  needsReview?: boolean;
  mahnungCount?: number;
  lastMahnungSentAt?: string;
  /** Gutschrift/Storno: Bezug auf die Ursprungsrechnung */
  correctsInvoiceId?: string | null;
  correctsInvoiceNumber?: string | null;
  correctionKind?: 'storno' | 'gutschrift' | null;
  createdAt: string;
  updatedAt: string;

  // Relations
  patient?: Patient;
  caregiver?: Caregiver;
  shift?: any; // ManualShift type
  items?: InvoiceItem[];

  // Computed fields from backend
  daysUntilDue?: number | null;
  assignedStaffName?: string | null;
  assignedStaffEmail?: string | null;
}

export interface InvoiceItem {
  id?: string;
  description: string;
  /** Optionale Zusatzzeile (PDF: klein und grau unter der Beschreibung). */
  note?: string | null;
  unitPrice: number;
  quantity: number;
  taxRate?: number;
  netAmount?: number;
  taxAmount?: number;
  grossAmount?: number;
  category?: string;
}

export interface CreateInvoiceFormData {
  type: InvoiceType;
  pricingMode?: InvoicePricingMode;
  patientId?: string; // Optional - agency-to-caregiver invoices don't have patients
  caregiverId?: string; // Optional - agency-to-client invoices don't have caregivers
  shiftId?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  totalDays: number;
  dailyRate: number;
  totalAmount: number;
  invoiceDate: string;
  dueDate?: string;
  notes?: string;
  items?: InvoiceItem[];
  invoicePrefix?: string;
  invoiceRecipient?: string;
  creationMode?: string;
}

export interface UpdateInvoiceFormData extends Partial<CreateInvoiceFormData> {
  status?: InvoiceStatus;
  paymentMethod?: string;
  paymentReference?: string;
}

export interface InvoiceQueryParams {
  search?: string;
  patientId?: string;
  caregiverId?: string;
  status?: InvoiceStatus;
  type?: InvoiceType;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  agencyId?: string;
  hasMahnung?: boolean;
  /** Nur Entwürfe wiederkehrender Rechnungen, die vor dem Versand geprüft werden. */
  needsReview?: boolean;
  prefixes?: string[];
  /** Rechnungen an Betreuer:innen bzw. an Klient:innen */
  direction?: 'client' | 'caregiver';
}

export interface InvoiceStatistics {
  totalInvoices: number;
  draftInvoices: number;
  sentInvoices: number;
  paidInvoices: number;
  overdueInvoices: number;
  totalAmount: number;
  paidAmount: number;
  pendingAmount: number;
}

export interface MarkPaymentData {
  isPaid: boolean;
  paymentMethod?: string;
  paymentReference?: string;
  notes?: string;
}

// Template types
export enum TemplateType {
  CAREGIVER = 'caregiver',
  PATIENT = 'patient',
  GENERAL = 'general',
}

export interface Template {
  id: string;
  name: string;
  description?: string;
  filename: string;
  filepath: string;
  filesize: number;
  mimetype: string;
  type: TemplateType;
  fields?: PDFFieldInfo[];
  fieldMappings?: Record<string, string>;
  agencyId: string;
  agency: Agency;
  isActive: boolean;
  /** Vorlage wird mit Bearbeitungsstatus gefuehrt (offen/erledigt/geloescht). */
  hasStatus?: boolean;
  /** Ausfuell-Dialog bietet einen zweiten Betreuer an (caregiver2_*-Platzhalter). */
  hasSecondCaregiver?: boolean;
  /** Spalte "Unterschrieben (Upload)" in Ausgefüllte Vorlagen. */
  uploadSignedEnabled?: boolean;
  /** Spalte "Bestätigung" (Upload) in Ausgefüllte Vorlagen. */
  uploadConfirmationEnabled?: boolean;
  /** Nur mit hasStatus: dieser Upload setzt den Status auf "erledigt". */
  autoDoneOn?: 'signed' | 'confirmation' | null;
  /** Profil-Dokumente, die beim Versand automatisch angehaengt werden. */
  autoAttachDocuments?: string[];
  /** Zugeordnete E-Mail-Vorlage der Agentur (customEmailTemplates). */
  emailTemplateId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type FilledTemplateStatus = 'open' | 'done' | 'deleted';

export interface PDFFieldInfo {
  name: string;
  type: 'text' | 'checkbox' | 'dropdown' | 'radio';
  value?: string | boolean;
  options?: string[];
}

export interface CreateTemplateDto {
  name: string;
  description?: string;
  type: TemplateType;
  agencyId?: string;
  fieldMappings?: Record<string, string>;
  isActive?: boolean;
  hasStatus?: boolean;
  hasSecondCaregiver?: boolean;
  uploadSignedEnabled?: boolean;
  uploadConfirmationEnabled?: boolean;
  autoDoneOn?: 'signed' | 'confirmation' | null;
  autoAttachDocuments?: string[];
  emailTemplateId?: string | null;
}

export interface FillTemplateDto {
  templateId: string;
  patientId?: string;
  caregiverId?: string;
  /** Optional "Betreuer 2" — only used when the template has hasSecondCaregiver. */
  caregiver2Id?: string;
  manualShiftId?: string;
  customFields?: Record<string, any>;
  outputFilename?: string;
  signatureDataUrl?: string;
}

export interface BatchFillRecipient {
  patientId?: string;
  caregiverId?: string;
}

export interface BatchFillTemplateDto {
  templateIds: string[];
  recipients: BatchFillRecipient[];
  customFields?: Record<string, any>;
}

export interface BatchFillResultItem {
  templateId: string;
  templateName: string;
  patientId?: string;
  patientName?: string;
  caregiverId?: string;
  caregiverName?: string;
  success: boolean;
  filledTemplateId?: string;
  filledPdfPath?: string;
  outputFilename?: string;
  error?: string;
}

export interface PreviewTemplateDto {
  templateId: string;
  patientId?: string;
  caregiverId?: string;
  /** Optional "Betreuer 2" — only used when the template has hasSecondCaregiver. */
  caregiver2Id?: string;
  manualShiftId?: string;
  customFields?: Record<string, any>;
  outputFilename?: string;
  signatureDataUrl?: string;
}

/**
 * Eine frei benannte E-Mail-Vorlage der Agentur, angelegt unter
 * "E-Mail-Vorlagen" in den Einstellungen.
 */
export interface CustomEmailTemplate {
  id: string;
  name: string;
  subject: string;
  message: string;
  /** Profil-Dokumente, die diese Vorlage immer mitschickt. */
  autoAttachDocuments?: string[];
}

export interface FilledTemplate {
  id: string;
  signedUploadFileId?: string | null;
  signedUploadFileName?: string | null;
  confirmationFileId?: string | null;
  confirmationFileName?: string | null;
  filename: string;
  filepath: string;
  originalFilename: string;
  filesize: number;
  patientId?: string;
  patientName?: string;
  caregiverId?: string;
  caregiverName?: string;
  /** Fallnummer of the Turnus picked when filling (null for older documents). */
  caseNumber?: number | null;
  templateId: string;
  template: Template;
  agencyId: string;
  agency: Agency;
  createdBy: string;
  status?: FilledTemplateStatus | null;
  createdAt: string;
  updatedAt: string;
  // Signature link fields
  hasClientSignatureField: boolean;
  hasCaregiverSignatureField: boolean;
  hasAgencySignatureField: boolean;
  clientSignatureToken?: string;
  caregiverSignatureToken?: string;
  clientSignatureLink?: string;
  caregiverSignatureLink?: string;
  // Address the signature link was actually sent to - the only record that a
  // filled template has left the house.
  clientSignatureEmail?: string;
  caregiverSignatureEmail?: string;
  clientSignatureCompleted: boolean;
  caregiverSignatureCompleted: boolean;
  agencySignatureCompleted: boolean;
  signatureLinksExpiresAt?: string;
  // Client signature metadata
  clientSignatureTimestamp?: string;
  clientSignatureIpAddress?: string;
  clientSignatureUserAgent?: string;
  // Caregiver signature metadata
  caregiverSignatureTimestamp?: string;
  caregiverSignatureIpAddress?: string;
  caregiverSignatureUserAgent?: string;
  // Agency signature metadata
  agencySignatureTimestamp?: string;
  agencySignatureIpAddress?: string;
  agencySignatureUserAgent?: string;
  agencySignatureUserId?: string;
  agencySignatureUserName?: string;
  // Manual tracking maintained by agency staff (see DocumentTemplateList).
  sentAt?: string | null;
  sentBy?: string | null;
  /** Empfänger des letzten E-Mail-Versands aus dem System (kommagetrennt). */
  sentTo?: string | null;
  responseAt?: string | null;
  responseBy?: string | null;
  trackingNote?: string | null;
}

export interface SendTemplateDto {
  recipients: string[];
  subject: string;
  message?: string;
  filledPdfPath: string;
  attachmentName?: string;
  additionalFileIds?: string[];
  additionalFilledTemplateIds?: string[];
  /** false = ohne E-Mail-Signatur der Agentur. */
  includeSignature?: boolean;
}

// Navigation and sidebar types
export interface SidebarMenuItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  roles: UserRole[];
  permission?: keyof UserPermissions;
  children?: SidebarMenuItem[];
  disabled?: boolean;
}

// API response types
export interface ApiResponse<T> {
  data: T;
  message: string;
  status: 'success' | 'error';
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Form types
export interface LoginFormData {
  email: string;
  password: string;
  captcha?: string;
}

export type AgencyCountry = 'AT' | 'DE' | 'CH';

export interface CreateAgencyFormData {
  name: string;
  country?: AgencyCountry;
  invoiceCurrency?: 'EUR' | 'CHF';
  emailSignatureEnabled?: boolean;
  emailSignatureHtml?: string | null;
  emailSignatureText?: string | null;
  addressStreet: string;
  addressHouseNumber: string;
  addressStaircase?: string;
  addressFloor?: string;
  addressDoor?: string;
  addressAdditionalInfo?: string;
  addressPostalCode: string;
  addressCity: string;
  addressCountry: string;
  phone: string;
  email: string;
  licenseNumber: string;
  logoUrl?: string; // Firebase Storage URL for agency logo
  signatureDataUrl?: string | null; // Stored agency signature (PNG data URL) for one-click signing
  showLogoOnInvoices?: boolean; // Whether to display logo on invoices
  logoSizeOnInvoices?: number; // Logo size in pixels for invoices (40-150)
  /** HerzensApp: Hinweis auf offene/verschobene Aufgaben (Standard an). */
  herzensappOpenTaskWarning?: boolean;
  /** Ausweis-/Einkommensnachweis per OCR vorausfuellen (Standard an). */
  documentOcrEnabled?: boolean;
  adminEmail: string;
  adminFirstName: string;
  adminLastName: string;
  adminPassword: string;
  sendInvite?: boolean;
  // Invoice Settings
  invoiceBankName?: string;
  invoiceBankIban?: string;
  invoiceBankBic?: string;
  invoiceNumberPrefix?: string;
  invoiceHeader?: string;
  invoiceFooter?: string;
  manualInvoiceDescription?: string;
  invoiceServiceLabel?: string;
  invoiceBillingAddress?: string;
  invoiceVatNumber?: string;
  invoiceDueDateDays?: number;
  /** 'days' = Rechnungsdatum + N Tage (Standard), 'day_of_month' = fester Zahltag. */
  invoiceDueMode?: 'days' | 'day_of_month';
  /** Fester Zahltag 1–31, nur bei invoiceDueMode = 'day_of_month'. */
  invoiceDueDayOfMonth?: number | null;
  invoiceCounter?: number;
  // VAT Settings
  vatRate?: number;
  vatIncluded?: boolean;
  vatEnabled?: boolean;
  // Default Daily Fee Settings
  defaultDailyFeeToAgency?: number;
  // Company information
  companyRegisterNumber?: string;
  // Managing Director information
  mdFirstName?: string;
  mdLastName?: string;
  // Bank account owner (different from bank name)
  bankAccountOwner?: string;
  // Name und Anschrift Bevollmächtigte
  bevollmaechtigte?: string;
  // Email Template Settings
  emailTemplateSubject?: string;
  emailTemplateMessage?: string;
  emailTemplateEnabled?: boolean;
  // Theme/Branding Settings
  primaryColor?: string;
}

// Loading and error state types
export interface LoadingState {
  isLoading: boolean;
  error: string | null;
}

// Task Management Types
export enum TaskStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

export enum TaskPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent'
}

export enum AssigneeType {
  CAREGIVER = 'caregiver',
  STAFF = 'staff'
}

export interface Task {
  id: string;
  title: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority;
  assigneeType: AssigneeType;
  assigneeId: string;
  assigneeName: string;
  dueDate?: string;
  completedDate?: string;
  assignedById: string;
  assignedByName: string;
  agencyId: string;
  notes?: string;
  metadata?: Record<string, any>;
  patientId?: string | null;
  patientName?: string | null;
  // Optional Betreuer link — the task concerns this caregiver (never an assignment)
  caregiverId?: string | null;
  caregiverName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTaskFormData extends Omit<Task, 'id' | 'createdAt' | 'updatedAt'> {
  dueDate?: string;
}

export interface UpdateTaskFormData extends Partial<CreateTaskFormData> {
  completedDate?: string;
}

export interface TaskQueryParams {
  search?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  assigneeType?: AssigneeType;
  assigneeId?: string;
  assignedById?: string;
  agencyId?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  patientId?: string;
  caregiverId?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface TaskStatistics {
  totalTasks: number;
  pendingTasks: number;
  inProgressTasks: number;
  completedTasks: number;
  overdueTasks: number;
  completionRate: number;
}

// Knowledge / Kontaktprotokoll Types
export enum KontaktType {
  TELEFONAT = 'telefonat',
  PERSOENLICHER_BESUCH = 'persoenlicher_besuch',
  WHATSAPP = 'whatsapp',
  EMAIL = 'email',
  BENUTZERDEFINIERT = 'benutzerdefiniert',
}

export enum EintragTyp {
  KONTROLLE = 'kontrolle',
  BESCHWERDE = 'beschwerde',
  RUECKMELDUNG = 'rueckmeldung',
  VISITE = 'visite',
  INFO = 'info',
  NOTIZ = 'notiz',
  BENUTZERDEFINIERT = 'benutzerdefiniert',
}

export interface KnowledgeEntry {
  id: string;
  agencyId: string;
  contactType: KontaktType;
  contactTypeCustom?: string;
  entryType: EintragTyp;
  entryTypeCustom?: string;
  datum: string;
  patientId?: string;
  patientName?: string;
  caregiverId?: string;
  caregiverName?: string;
  staffId?: string;
  staffName?: string;
  status: TaskStatus;
  text?: string;
  createdById: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateKnowledgeEntryFormData {
  contactType: KontaktType;
  contactTypeCustom?: string;
  entryType: EintragTyp;
  entryTypeCustom?: string;
  datum?: string;
  patientId?: string;
  patientName?: string;
  caregiverId?: string;
  caregiverName?: string;
  staffId?: string;
  staffName?: string;
  status?: TaskStatus;
  text?: string;
}

export interface KnowledgeQueryParams {
  patientId?: string;
  caregiverId?: string;
  staffId?: string;
  status?: TaskStatus;
  contactType?: KontaktType;
  entryType?: EintragTyp;
  datumFrom?: string;
  datumTo?: string;
  search?: string;
  page?: number;
  limit?: number;
}

// Family invoice schedule
export enum IntervalType {
  DAYS = 'days',
  WEEKS = 'weeks',
  MONTHS = 'months',
  YEARS = 'years',
}

export interface FamilyInvoiceScheduleAmount {
  id: string;
  familyScheduleId: string;
  amount: number;
  description: string;
  taxRate?: number;
  note?: string | null;
  perDay?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FamilyInvoiceSchedule {
  id: string;
  patientId?: string | null;
  caregiverId?: string | null;
  agencyId: string;
  startDate: string;
  intervalType: IntervalType;
  intervalValue: number;
  intervalDays: number;
  dailyRate: number;
  lastInvoiceDate?: string;
  nextInvoiceDate: string;
  isActive: boolean;
  createFirstInvoiceImmediately?: boolean;
  reviewBeforeSend?: boolean;
  /** 'advance' (Standard) oder 'arrears_turnus' = im Nachhinein nach Turnus-Tagen */
  billingMode?: 'advance' | 'arrears_turnus';
  invoiceRecipient?: string;
  creationMode?: string; // 'agency-to-client' | 'agency-to-caregiver'
  createdAt: string;
  updatedAt: string;

  // Relations
  patient?: Patient;
  caregiver?: Caregiver;
  amounts?: FamilyInvoiceScheduleAmount[];
}

export interface CreateFamilyScheduleFormData {
  patientId?: string;
  caregiverId?: string;
  agencyId?: string;
  startDate: string;
  intervalType: IntervalType;
  intervalValue: number;
  dailyRate?: number; // Keep for backward compatibility
  createFirstInvoiceImmediately?: boolean;
  reviewBeforeSend?: boolean;
  billingMode?: 'advance' | 'arrears_turnus';
  invoiceRecipient?: string;
  amounts: Array<{ amount: number; description: string; taxRate?: number; note?: string | null; perDay?: boolean }>;
}

// Patient Checklist Types
export interface PatientChecklistItem {
  id: string;
  checklistId: string;
  title: string;
  description?: string;
  isChecked: boolean;
  checkedById?: string;
  checkedByName?: string;
  checkedAt?: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface PatientChecklist {
  id: string;
  patientId: string;
  agencyId: string;
  title: string;
  description?: string;
  notice?: string;
  createdById: string;
  createdByName: string;
  isActive: boolean;
  items: PatientChecklistItem[];
  createdAt: string;
  updatedAt: string;
}

export interface CreatePatientChecklistItemDto {
  title: string;
  description?: string;
  sortOrder?: number;
}

export interface CreatePatientChecklistDto {
  title: string;
  description?: string;
  notice?: string;
  items: CreatePatientChecklistItemDto[];
}

export interface UpdatePatientChecklistDto extends Partial<CreatePatientChecklistDto> {
  isActive?: boolean;
}

export interface CheckPatientChecklistItemDto {
  isChecked: boolean;
}

// Caregiver Checklist Types
export interface CaregiverChecklistItem {
  id: string;
  checklistId: string;
  title: string;
  description?: string;
  isChecked: boolean;
  checkedById?: string;
  checkedByName?: string;
  checkedAt?: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CaregiverChecklist {
  id: string;
  caregiverId: string;
  agencyId: string;
  title: string;
  description?: string;
  notice?: string;
  createdById: string;
  createdByName: string;
  isActive: boolean;
  items: CaregiverChecklistItem[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateCaregiverChecklistItemDto {
  title: string;
  description?: string;
  sortOrder?: number;
}

export interface CreateCaregiverChecklistDto {
  title: string;
  description?: string;
  notice?: string;
  items: CreateCaregiverChecklistItemDto[];
}

export interface UpdateCaregiverChecklistDto extends Partial<CreateCaregiverChecklistDto> {
  isActive?: boolean;
}

export interface CheckCaregiverChecklistItemDto {
  isChecked: boolean;
}

// Shift Checklist Types
export interface ShiftChecklistItem {
  id: string;
  checklistId: string;
  title: string;
  description?: string;
  isChecked: boolean;
  checkedById?: string;
  checkedByName?: string;
  checkedAt?: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ShiftChecklist {
  id: string;
  shiftId: string;
  caseNumber: number;
  agencyId: string;
  title: string;
  description?: string;
  notice?: string;
  createdById: string;
  createdByName: string;
  isActive: boolean;
  items: ShiftChecklistItem[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateShiftChecklistItemDto {
  title: string;
  description?: string;
  sortOrder?: number;
}

export interface CreateShiftChecklistDto {
  title: string;
  description?: string;
  notice?: string;
  items: CreateShiftChecklistItemDto[];
}

export interface UpdateShiftChecklistDto extends Partial<CreateShiftChecklistDto> {
  isActive?: boolean;
}

export interface CheckShiftChecklistItemDto {
  isChecked: boolean;
}

// Checklist Template Types
export interface ChecklistTemplateItem {
  id: string;
  templateId: string;
  title: string;
  description?: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ChecklistTemplate {
  id: string;
  agencyId: string;
  name: string;
  title: string;
  description?: string;
  notice?: string;
  createdById: string;
  createdByName: string;
  isActive: boolean;
  isDefaultForPatient: boolean;
  isDefaultForCaregiver: boolean;
  items: ChecklistTemplateItem[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateChecklistTemplateItemDto {
  title: string;
  description?: string;
  sortOrder?: number;
}

export interface CreateChecklistTemplateDto {
  name: string;
  title: string;
  description?: string;
  notice?: string;
  items: CreateChecklistTemplateItemDto[];
}

export interface UpdateChecklistTemplateDto extends Partial<CreateChecklistTemplateDto> {
  isActive?: boolean;
  isDefaultForPatient?: boolean;
  isDefaultForCaregiver?: boolean;
}

// Email Log types
export interface EmailLog {
  id: string;
  agencyId?: string;
  subject: string;
  recipients: string[];
  htmlContent?: string;
  textContent?: string;
  attachmentNames?: string[];
  status: 'sent' | 'failed';
  errorMessage?: string;
  emailType?: string;
  sentAt: string;
  /** Nur bei KI-E-Mail-Einträgen: wer gesendet hat. */
  sentByEmail?: string | null;
}

export type EmailType = 'invoice' | 'template' | 'signature' | 'matching' | 'shift' | 'mahnung' | 'freigegeben' | 'ai_reply' | 'ai_compose' | 'other';
